import type { DataOption } from '@/common.types'
import type {
	FieldPath,
	FieldValues,
	UseControllerProps,
} from 'react-hook-form'

import React from 'react'
import { flatten } from 'lodash/fp'
import {
	CheckIcon,
	ChevronsUpDownIcon,
	HelpCircleIcon,
	LoaderCircleIcon,
} from 'lucide-react'
import { useFormContext } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import {
	Command,
	CommandEmpty,
	CommandGroup,
	CommandInput,
	CommandItem,
	CommandList,
} from '@/shared/components/ui/command'
import {
	FormControl,
	FormDescription,
	FormField,
	FormItem,
	FormLabel,
	FormMessage,
} from '@/shared/components/ui/form'
import {
	InputBase,
	InputBaseAdornment,
	InputBaseControl,
	InputBaseFlexWrapper,
} from '@/shared/components/ui/input-base'
import {
	Popover,
	PopoverContent,
	PopoverTrigger,
} from '@/shared/components/ui/popover'
import {
	Tooltip,
	TooltipContent,
	TooltipTrigger,
} from '@/shared/components/ui/tooltip'
import {
	Virtualized,
	VirtualizedVirtualizer,
} from '@/shared/components/ui/virtualizer'
import { cn } from '@/shared/lib/utils'

export type GroupBase<Option> = {
	options: Option[]
	label?: string
}
export type OptionsOrGroups<Option, Group extends GroupBase<Option>> = (
	Option | Group
)[]
export default function RHFCombobox<
	TFieldValues extends FieldValues = FieldValues,
	TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
	OptionType extends DataOption = DataOption,
>({
	name,
	label,
	hint,
	description,
	defaultValue,
	shouldUnregister,
	options,
	placeholder,
	className,
	disabled,
	isLoading,
	isOptionDisabled,
}: UseControllerProps<TFieldValues, TName> & {
	options: OptionsOrGroups<OptionType, GroupBase<OptionType>>
} & {
	placeholder?: string
	className?: string
	disabled?: boolean
	label: string
	hint?: string
	description?: string
	isLoading?: boolean
	isOptionDisabled?: (option: OptionType) => boolean
}) {
	const { t } = useTranslation()

	const { control } = useFormContext()
	const [open, setOpen] = React.useState(false)
	const flatOptions = React.useMemo(
		() =>
			flatten(
				options?.map((v) => {
					if ((v as GroupBase<OptionType>).options) {
						return (v as GroupBase<OptionType>).options
					} else {
						return [v] as OptionType[]
					}
				})
			) as OptionType[],
		[options]
	)

	return (
		<FormField
			name={name}
			defaultValue={defaultValue}
			shouldUnregister={shouldUnregister}
			control={control}
			render={({ field, fieldState: { invalid } }) => {
				const valueLabel = flatOptions.find(
					(language) => language.value === field.value
				)?.label

				return (
					<FormItem>
						<FormLabel>
							{label}
							{hint && (
								<Tooltip>
									<TooltipTrigger asChild>
										<HelpCircleIcon className='size-3 text-muted-foreground' />
									</TooltipTrigger>
									<TooltipContent side='top'>{hint}</TooltipContent>
								</Tooltip>
							)}
						</FormLabel>
						<Popover open={open} onOpenChange={setOpen}>
							<PopoverTrigger asChild>
								<FormControl data-name={name}>
									<InputBase
										asChild
										role='combobox'
										className={className}
										aria-expanded={open}
										data-invalid={invalid}
										disabled={disabled}
									>
										<button disabled={disabled}>
											<InputBaseControl>
												<InputBaseFlexWrapper
													className={cn(
														'truncate',
														!valueLabel && placeholder && 'text-placeholder'
													)}
												>
													{valueLabel || placeholder}
												</InputBaseFlexWrapper>
											</InputBaseControl>
											<InputBaseAdornment>
												<ChevronsUpDownIcon className='size-4 text-muted-foreground opacity-50' />
											</InputBaseAdornment>
										</button>
									</InputBase>
								</FormControl>
							</PopoverTrigger>

							<PopoverContent className='w-(--radix-popover-trigger-width) p-0'>
								<Command>
									<CommandInput
										placeholder={t('placeholder.search_options', {
											defaultValue: 'Search options...',
										})}
									/>
									<Virtualized asChild>
										<CommandList>
											<CommandEmpty>
												{t('no_options', { defaultValue: 'No options found' })}
											</CommandEmpty>
											{isLoading ? (
												<div className='flex items-center justify-center py-4'>
													<LoaderCircleIcon className='size-4 animate-spin opacity-50' />
												</div>
											) : (
												<CommandGroup>
													<VirtualizedVirtualizer>
														{(options as OptionType[]).map((option) => (
															<CommandItem
																data-value={option.value}
																value={option.label}
																key={option.value}
																keywords={[option.label]}
																disabled={isOptionDisabled?.(option)}
																onSelect={() => {
																	field.onChange(option.value)
																	setOpen(false)
																}}
															>
																{option.label}
																<CheckIcon
																	className={cn(
																		'ml-auto',
																		option.value === field.value
																			? 'opacity-100'
																			: 'opacity-0'
																	)}
																/>
															</CommandItem>
														))}
													</VirtualizedVirtualizer>
												</CommandGroup>
											)}
										</CommandList>
									</Virtualized>
								</Command>
								{/* )} */}
							</PopoverContent>
						</Popover>
						{!!description && <FormDescription>{description}</FormDescription>}
						<FormMessage />
					</FormItem>
				)
			}}
		/>
	)
}
